import React, { useEffect, useState } from 'react';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageStore } from 'src/context/StowageContext';
import ChangeLogRotationComponent from './ChangeLogRotation';
import { ChangeLogTree, ClosedOrCompledVoyagesWrapper, DescriptionText } from './ChangeLog.styles';
import CollapseComponent from 'src/components/common/collapse/CollapseComponent';
import { ComparisonVoyage, mapToComparisonVoyage, removeExtraDataFromData } from 'src/utils/stowage/stowagePlanComparison/comparisonMapper';
import { filterSyncChangesByOpenOrClosed, getNumberOfClosedSyncChanges } from 'src/utils/stowage/stowagePlanComparison/compareRotationUtilsV2';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { useAuthStore } from 'src/context/AuthContext';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { deepCopy } from 'src/utils/objectUtils';
import { PanelDimensions } from 'src/types/ViewModel/Stowage/PanelDimensions';
import { usePositionFromClick } from 'src/hooks/stowage/usePositionFromClick';
import theme from 'src/theme';

export type ChangeLogProps = {
    isChangeLogOpen: boolean;
    stowagePlanSyncChanges: StowagePlanChange[];
    title: string;
    syncedDateTimeField: string;
    shouldFilterClosedOrCompletedVoyages?: boolean;
    stowagePlanFields?: Partial<StowagePlan>;
    description?: string;
    isSyncButtonDisabled?: boolean;
    shouldAddChanges?: boolean;
};

const ChangeLogComponent: React.FC = () => {
    const { triggerChangeLogProps, setTriggerChangeLogProps, activeStowagePlan, stowagePlans, mouseClickPosition } = useStowageStore();
    const { portNameList, berthNameList, imosRotationPortCallList, imosRotationCargoList } = useRotationStore();
    const { saveStowageData, updateStowagePlan } = usePostStowageApi();
    const [isClosedOrCompletedVoyagesOpen, setIsClosedOrCompletedVoyagesOpen] = useState<boolean>(false);
    const [isSyncingChanges, setIsSyncingChanges] = useState<boolean>(false);
    const [hasManualRotationChanges, setHasManualRotationChanges] = useState<boolean>(false);
    const [openSyncVoyages, setOpenSyncVoyages] = useState<ComparisonVoyage[]>([]);
    const [closedSyncVoyages, setClosedSyncVoyages] = useState<ComparisonVoyage[]>([]);
    const [stowageItems, setStowageItems] = useState<Stowage[]>([]);
    const [adjustedCargoTemperatures, setAdjustedCargoTemperatures] = useState<AdjustedCargoTemperature[]>([]);
    const [blockedTanks, setBlockedTanks] = useState<BlockedTank[]>([]);
    const { loggedInUser } = useAuthStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();

    // =============  Use Effects ================

    useEffect(() => {
        if (
            !triggerChangeLogProps?.isChangeLogOpen ||
            !stowagePlans ||
            !activeStowagePlan ||
            !portNameList ||
            !berthNameList ||
            !imosRotationPortCallList ||
            !imosRotationCargoList ||
            isSyncingChanges
        )
            return;
        mapChanges();
    }, [
        triggerChangeLogProps,
        stowagePlans,
        activeStowagePlan,
        portNameList,
        berthNameList,
        imosRotationPortCallList,
        imosRotationCargoList,
        isSyncingChanges,
    ]);

    const checkForManualRotationChanges = (comparisonVoyages: ComparisonVoyage[]) => {
        const hasManualChange = comparisonVoyages.some((v) => {
            const portCalls = activeStowagePlan.rotation.filter((pc) => pc.voyageNumber === v.voyageNumber);
            return portCalls.some((pc) => pc.hasChangedSequence || pc.berthCalls.some((bc) => bc.hasChangedSequence));
        });
        setHasManualRotationChanges(hasManualChange);
    };

    const mapChanges = () => {
        if (!triggerChangeLogProps?.isChangeLogOpen || !activeStowagePlan) return;

        // Filter changes first based on open/closed status
        let filteredChanges = triggerChangeLogProps?.stowagePlanSyncChanges;
        let closedChanges: StowagePlanChange[] = [];

        if (triggerChangeLogProps?.shouldFilterClosedOrCompletedVoyages) {
            const openChanges = filterSyncChangesByOpenOrClosed(filteredChanges ?? [], false);
            closedChanges = filterSyncChangesByOpenOrClosed(filteredChanges ?? [], true);
            filteredChanges = openChanges;
        }

        // Map the filtered changes to voyages
        const {
            voyages: openVoyages,
            stowageItems,
            adjustedCargoTemperatures,
            blockedTanks,
        } = mapToComparisonVoyage(filteredChanges, activeStowagePlan, imosRotationPortCallList, imosRotationCargoList, portNameList, berthNameList);

        // Map the closed changes to voyages if they exist
        let closedVoyages: ComparisonVoyage[] = [];
        if (closedChanges.length > 0) {
            const closedResult = mapToComparisonVoyage(
                closedChanges,
                activeStowagePlan,
                imosRotationPortCallList,
                imosRotationCargoList,
                portNameList,
                berthNameList
            );
            closedVoyages = closedResult.voyages;
        }

        checkForManualRotationChanges(openVoyages);
        setOpenSyncVoyages([...openVoyages]);
        setClosedSyncVoyages([...closedVoyages]);
        setStowageItems([...stowageItems]);
        setAdjustedCargoTemperatures([...adjustedCargoTemperatures]);
        setBlockedTanks([...blockedTanks]);

        return [...openVoyages, ...closedVoyages];
    };

    const onApplyAllChanges = async () => {
        if (triggerChangeLogProps?.stowagePlanSyncChanges?.length === 0 || openSyncVoyages?.length === 0) return;
        const stowagePlanSyncChanges = deepCopy(triggerChangeLogProps?.stowagePlanSyncChanges);
        setIsSyncingChanges(true);
        const syncChanges = triggerChangeLogProps?.shouldFilterClosedOrCompletedVoyages
            ? filterSyncChangesByOpenOrClosed(stowagePlanSyncChanges ?? [], false)
            : stowagePlanSyncChanges ?? [];

        syncChanges.forEach((change) => {
            change.data = removeExtraDataFromData(change.data);
        });

        let updatedStowagePlan = triggerChangeLogProps.shouldAddChanges
            ? (await saveStowageData(activeStowagePlan, syncChanges)).updatedStowagePlan
            : { ...activeStowagePlan };
        updatedStowagePlan.lastUpdatedUtc = getDateNow();
        updatedStowagePlan.lastUpdatedBy = loggedInUser.userName;
        updatedStowagePlan[triggerChangeLogProps.syncedDateTimeField] = getDateNow();
        if (triggerChangeLogProps.stowagePlanFields) {
            updatedStowagePlan = {
                ...updatedStowagePlan,
                ...triggerChangeLogProps.stowagePlanFields,
            };
        }
        await updateStowagePlan(updatedStowagePlan);
        if (!triggerChangeLogProps.shouldAddChanges) {
            setQueryParamStowagePlan(updatedStowagePlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        }
        setIsSyncingChanges(false);
        onClosePanel();
    };

    const onClosePanel = () => {
        setTriggerChangeLogProps(null);
    };

    const numberOfClosedOrCompletedVoyages = getNumberOfClosedSyncChanges(triggerChangeLogProps?.stowagePlanSyncChanges ?? []);

    const panelDimensions: PanelDimensions = {
        width: PANEL_CONSTANTS.SMALL_WIDTH,
        height: 350,
    };

    const panelPosition = usePositionFromClick(mouseClickPosition, panelDimensions);

    return (
        triggerChangeLogProps?.isChangeLogOpen && (
            <OdfjellPanel
                headerTitle={triggerChangeLogProps.title}
                defaultPosition="right"
                initialPosition={mouseClickPosition ? panelPosition : undefined}
                actionButtonText="Synchronize changes"
                onAction={onApplyAllChanges}
                onCancel={onClosePanel}
                isActionDisabled={triggerChangeLogProps.isSyncButtonDisabled}
                showCancelButton={true}
                panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
            >
                {triggerChangeLogProps.description && (
                    <DescriptionText>
                        {triggerChangeLogProps.description}
                        <br />
                    </DescriptionText>
                )}
                <ChangeLogTree>
                    {openSyncVoyages.length > 0 && (
                        <ChangeLogRotationComponent
                            groupedImosSyncData={openSyncVoyages}
                            stowageItems={stowageItems}
                            blockedTanks={blockedTanks}
                            adjustedCargoTemperatures={adjustedCargoTemperatures}
                        />
                    )}
                    {closedSyncVoyages?.length > 0 && (
                        <ClosedOrCompledVoyagesWrapper>
                            <CollapseComponent
                                title={'Note: closed or completed voyages will not be synced'}
                                headerText={`Changes in closed or completed voyages (${numberOfClosedOrCompletedVoyages})`}
                                isOpen={isClosedOrCompletedVoyagesOpen}
                                setisOpen={setIsClosedOrCompletedVoyagesOpen}
                            >
                                <ChangeLogRotationComponent
                                    groupedImosSyncData={closedSyncVoyages}
                                    stowageItems={stowageItems}
                                    blockedTanks={blockedTanks}
                                    adjustedCargoTemperatures={adjustedCargoTemperatures}
                                />
                            </CollapseComponent>
                        </ClosedOrCompledVoyagesWrapper>
                    )}
                </ChangeLogTree>
                {hasManualRotationChanges && (
                    <DescriptionText style={{ color: theme.colors.text.warningDark }}>
                        Rotation changes have been detected in this plan. If the plan is correct, please update the rotation in IMOS before syncing to
                        avoid data mismatch. Syncing this will revert the rotation to the IMOS rotation.
                    </DescriptionText>
                )}
            </OdfjellPanel>
        )
    );
};

export default ChangeLogComponent;
